// Fronta „K vyřízení“ (rozhodnutí 05/18, W24; kontrakt tag Fronty, 08 §3.42 `k_vyrizeni.cist`):
//   GET /k-vyrizeni?zalozka=&velikost=&polozka=&jen_pocty=   jedna fronta nad Ke kontrole, Hlášením, událostmi ISŘ
//                                     a kontrolami souladu, seřazená podle naléhavosti a lhůty; počty záložek jsou vždy
//                                     za celou záložku. Server vrací jen záložky, na které má účet právo — UI nic
//                                     nehádá z role (rozhodnutí 28. 9. 2026: co účet nesmí, to se nevykreslí).
// Fronta nic nezapisuje: položka se vyřizuje v obrazovce své sekce (`odkaz`) její stávající akcí.
import type { Schema } from '@ecoser/shared';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './klient';

export type KVyrizeni = Schema<'KVyrizeni'>;
export type PolozkaFronty = Schema<'KVyrizeniPolozka'>;
export type ZalozkaFronty = Schema<'KVyrizeniZalozka'>;
export type KlicZalozky = ZalozkaFronty['klic'];
export type Nalehavost = PolozkaFronty['nalehavost'];

export const SEKCE_K_VYRIZENI = 'k_vyrizeni';
export const KLIC_K_VYRIZENI = ['k-vyrizeni'] as const;

export const VYCHOZI_VELIKOST = 50;
export const MAX_VELIKOST = 200;
/** Jak často se fronta na otevřené obrazovce načte znovu (ms) — bez signálu o změně z jiného zařízení. */
export const OBNOVA_MS = 60_000;

/** Popisek naléhavosti pro čtečku obrazovky a nápovědu u tečky (barva sama nestačí). */
export const POPIS_NALEHAVOSTI: Readonly<Record<Nalehavost, string>> = {
  kriticka: 'Po lhůtě nebo kritické',
  vysoka: 'Spěchá',
  bezna: 'Běžné',
  nizka: 'Už se řeší',
};

export interface DotazFronty {
  zalozka?: KlicZalozky;
  velikost?: number;
  polozka?: string;
}

function sestavDotaz(dotaz: DotazFronty & { jen_pocty?: boolean }): string {
  const q = new URLSearchParams();
  if (dotaz.zalozka) q.set('zalozka', dotaz.zalozka);
  if (dotaz.velikost) q.set('velikost', String(dotaz.velikost));
  if (dotaz.polozka) q.set('polozka', dotaz.polozka);
  if (dotaz.jen_pocty) q.set('jen_pocty', 'true');
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function useKVyrizeni(dotaz: DotazFronty, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_K_VYRIZENI, 'seznam', dotaz] as const,
    queryFn: ({ signal }) => api.get<KVyrizeni>(`/k-vyrizeni${sestavDotaz(dotaz)}`, { signal }),
    enabled: povoleno,
    // Při přepnutí záložky zůstává předchozí seznam, dokud nepřijde nový (bez blikání prázdna).
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchInterval: OBNOVA_MS,
  });
}

export interface PoctyKVyrizeni {
  celkem: number;
  zalozky: readonly ZalozkaFronty[];
  /** Server aspoň jednou odpověděl. */
  nacteno: boolean;
}

/** Počty pro odznak v menu: bez položek (`jen_pocty`), obnovuje se s obrazovkou fronty. */
export function usePoctyKVyrizeni(povoleno: boolean): PoctyKVyrizeni {
  const dotaz = useQuery({
    queryKey: [...KLIC_K_VYRIZENI, 'pocty'] as const,
    queryFn: ({ signal }) =>
      api.get<KVyrizeni>(`/k-vyrizeni${sestavDotaz({ jen_pocty: true })}`, { signal }),
    enabled: povoleno,
    staleTime: 30_000,
    refetchInterval: OBNOVA_MS,
  });
  return {
    celkem: dotaz.data?.celkem ?? 0,
    zalozky: dotaz.data?.zalozky ?? [],
    nacteno: dotaz.data !== undefined,
  };
}

/** Po vyřízení položky v jiné obrazovce: fronta i odznak v menu se načtou znovu. */
export function obnovitFronty(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: KLIC_K_VYRIZENI });
}

/** Klíč položky `druh:id` pro zvonek a odkazy (`/k-vyrizeni?polozka=`). */
export function klicPolozky(druh: PolozkaFronty['druh'], id: string): string {
  return `${druh}:${id}`;
}

export function odkazNaPolozku(klic: string): string {
  return `/k-vyrizeni?polozka=${encodeURIComponent(klic)}`;
}
